import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Switch,
  TextField,
  Alert,
  Grid,
  Chip,
  IconButton,
  Tooltip,
  InputAdornment,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Avatar,
  useTheme,
  useMediaQuery,
  Paper,
  Divider
} from '@mui/material';
import {
  Add as AddIcon,
  Percent as PercentIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  Restaurant as RestaurantIcon,
  LocalShipping as DeliveryIcon,
  Receipt as ReceiptIcon,
  CardGiftcard as GiftIcon,
  LocalAtm as AtmIcon,
  Loyalty as LoyaltyIcon,
  Settings as SettingsIcon,
  AutoAwesome as AutoAwesomeIcon
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import EnhancedAddAddonDialog from '../dialogs/EnhancedAddAddonDialog';

const MotionCard = motion(Card);
const MotionBox = motion(Box);

const CartAddOnsManager = ({ settings, updateCartAddOn, onSave }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTablet = useMediaQuery(theme.breakpoints.between('md', 'lg'));

  // ✅ FIXED: Use array for rendering, but maintain object structure for data
  const [cartAddOns, setCartAddOns] = useState([]);
  const [editingAddOns, setEditingAddOns] = useState({});
  const [editDialog, setEditDialog] = useState({ open: false, addOn: null });
  const [showEnhancedDialog, setShowEnhancedDialog] = useState(false);

  // ✅ POS Config Color Theme
  const posConfigColors = {
    primary: '#667eea',
    secondary: '#764ba2',
    success: '#10b981',
    warning: '#f59e0b',
    error: '#ef4444',
    info: '#3b82f6'
  };

  // ✅ Add-on Category Colors (matching EnhancedAddAddonDialog)
  const addOnCategoryColors = {
    // Service & Fees
    service: '#667eea',
    delivery: '#06b6d4', 
    processing: '#8b5cf6',
    
    // Taxes & Charges
    tax: '#f59e0b',
    charge: '#ef4444',
    
    // Discounts & Promotions
    discount: '#ef4444',
    promotion: '#ec4899',
    loyalty: '#84cc16',
    
    // General
    general: '#6b7280'
  };

  // ✅ Add-on Type Colors (matching EnhancedAddAddonDialog types)
  const addOnTypeColors = {
    // Additions
    'fixed': '#10b981',
    'percentage': '#667eea',
    
    // Subtractions
    'fixed_subtract': '#ef4444',
    'percentage_subtract': '#f59e0b',
    'discount': '#ef4444'
  };

  // Available icons configuration
  const availableIcons = [
    { id: 'service', name: t('posConfig.cartAddOns.icons.service') || 'Service', icon: <RestaurantIcon />, value: 'RestaurantIcon', color: posConfigColors.primary },
    { id: 'tax', name: t('posConfig.cartAddOns.icons.tax') || 'Tax', icon: <PercentIcon />, value: 'PercentIcon', color: posConfigColors.warning },
    { id: 'money', name: t('posConfig.cartAddOns.icons.money') || 'Money', icon: <MoneyIcon />, value: 'MoneyIcon', color: posConfigColors.success },
    { id: 'trending_up', name: t('posConfig.cartAddOns.icons.charge') || 'Charge', icon: <TrendingUpIcon />, value: 'TrendingUpIcon', color: posConfigColors.warning },
    { id: 'trending_down', name: t('posConfig.cartAddOns.icons.discount') || 'Discount', icon: <TrendingDownIcon />, value: 'TrendingDownIcon', color: posConfigColors.error },
    { id: 'delivery', name: t('posConfig.cartAddOns.icons.delivery') || 'Delivery', icon: <DeliveryIcon />, value: 'DeliveryIcon', color: '#06b6d4' },
    { id: 'gift', name: t('posConfig.cartAddOns.icons.gift') || 'Gift', icon: <GiftIcon />, value: 'GiftIcon', color: '#ec4899' },
    { id: 'loyalty', name: t('posConfig.cartAddOns.icons.loyalty') || 'Loyalty', icon: <LoyaltyIcon />, value: 'LoyaltyIcon', color: '#84cc16' },
    { id: 'receipt', name: t('posConfig.cartAddOns.icons.receipt') || 'Receipt', icon: <ReceiptIcon />, value: 'ReceiptIcon', color: posConfigColors.info },
    { id: 'restaurant', name: t('posConfig.cartAddOns.icons.restaurant') || 'Restaurant', icon: <RestaurantIcon />, value: 'RestaurantIcon', color: '#f97316' },
    { id: 'atm', name: t('posConfig.cartAddOns.icons.atm') || 'ATM', icon: <AtmIcon />, value: 'AtmIcon', color: posConfigColors.success }
  ];

  // ✅ FIXED: Normalize cartAddOns from settings (object) to array for rendering
  useEffect(() => {
    if (settings?.cartAddOns) {
      console.log('🔧 CartAddOnsManager: Processing settings.cartAddOns:', settings.cartAddOns);
      
      let normalizedAddOns = [];
      
      if (Array.isArray(settings.cartAddOns)) {
        // ✅ Already array - use directly
        normalizedAddOns = settings.cartAddOns;
      } else if (typeof settings.cartAddOns === 'object') {
        // ✅ Object format - convert to array
        normalizedAddOns = Object.values(settings.cartAddOns);
      }
      
      // ✅ Remove duplicates by ID
      const uniqueAddOns = normalizedAddOns.reduce((acc, addOn) => {
        const existingIndex = acc.findIndex(item => item.id === addOn.id);
        if (existingIndex === -1) {
          acc.push(addOn);
        } else {
          // Keep the more recent one (with more properties)
          if (Object.keys(addOn).length > Object.keys(acc[existingIndex]).length) {
            acc[existingIndex] = addOn;
          }
        }
        return acc;
      }, []);
      
      console.log('✅ CartAddOnsManager: Normalized add-ons:', uniqueAddOns);
      setCartAddOns(uniqueAddOns);
    }
  }, [settings?.cartAddOns]);

  // ✅ FIXED: Handle toggle with proper object structure
  const handleToggleEnabled = async (addOnId, enabled) => {
    console.log('🔄 Toggle enabled:', { addOnId, enabled });
    
    try {
      // Update local state
      const updatedAddOns = cartAddOns.map(addOn => 
        addOn.id === addOnId ? { ...addOn, enabled } : addOn
      );
      setCartAddOns(updatedAddOns);
      
      // Update via callback (this should maintain object structure in settings)
      if (updateCartAddOn) {
        await updateCartAddOn(addOnId, { enabled });
      }
      
      console.log('✅ Add-on toggled successfully');
      
    } catch (error) {
      console.error('Error toggling add-on:', error);
      // Revert on error
      const revertedAddOns = cartAddOns.map(addOn => 
        addOn.id === addOnId ? { ...addOn, enabled: !enabled } : addOn
      );
      setCartAddOns(revertedAddOns);
    }
  };

  const handleStartEdit = (addOnId) => {
    const addOn = cartAddOns.find(a => a.id === addOnId);
    if (addOn) {
      setEditingAddOns(prev => ({
        ...prev,
        [addOnId]: { ...addOn }
      }));
    }
  };

  const handleEditAddOn = (addOn) => {
    setEditDialog({ open: true, addOn: { ...addOn } });
  };

  const handleCancelEdit = (addOnId) => {
    setEditingAddOns(prev => {
      const newState = { ...prev };
      delete newState[addOnId];
      return newState;
    });
  };

  const handleEditChange = (addOnId, field, value) => {
    setEditingAddOns(prev => ({
      ...prev,
      [addOnId]: {
        ...prev[addOnId],
        [field]: value
      }
    }));
  };

  const handleSaveAddOn = async (addOnId) => {
    try {
      const editedAddOn = editingAddOns[addOnId];
      if (!editedAddOn) return;

      const updatedAddOns = cartAddOns.map(addOn => 
        addOn.id === addOnId ? { ...addOn, ...editedAddOn } : addOn
      );
      setCartAddOns(updatedAddOns);

      if (updateCartAddOn) {
        await updateCartAddOn(addOnId, editedAddOn);
      }

      handleCancelEdit(addOnId);
      console.log('✅ Add-on saved successfully');
    } catch (error) {
      console.error('Error saving add-on:', error);
    }
  };

  // ✅ Handle new add-on creation from EnhancedAddAddonDialog
  const handleCreateAddOn = async (newAddOnData) => {
    try {
      console.log('🆕 Creating new add-on:', newAddOnData);
      
      // Generate unique ID
      const newId = `addon_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
      
      const newAddOn = {
        id: newId,
        name: newAddOnData.name,
        type: newAddOnData.type,
        value: parseFloat(newAddOnData.value) || 0,
        enabled: true,
        visible: true,
        editable: true,
        order: cartAddOns.length + 1,
        icon: newAddOnData.icon || 'MoneyIcon'
      };
      
      // ✅ Update local state immediately (optimistic update)
      const updatedAddOns = [...cartAddOns, newAddOn];
      setCartAddOns(updatedAddOns);
      
      // ✅ Save to backend
      if (onSave) {
        await onSave({ cartAddOns: updatedAddOns });
      } else if (updateCartAddOn) {
        // Fallback to individual update
        await updateCartAddOn(newId, newAddOn);
      }
      
      console.log('✅ New add-on created successfully');
      setShowEnhancedDialog(false);
      
    } catch (error) {
      console.error('❌ Error creating add-on:', error);
      // ✅ Revert local state on error
      setCartAddOns(cartAddOns);
      alert('Failed to create add-on. Please try again.');
    }
  };

  // ✅ FIXED: Handle deleting add-on
  const handleDeleteAddOn = async (addOnId) => {
    try {
      console.log('🗑️ Deleting add-on:', addOnId);
      
      // ✅ Update local state immediately (optimistic update)
      const updatedAddOns = cartAddOns.filter(addOn => addOn.id !== addOnId);
      setCartAddOns(updatedAddOns);
      
      // ✅ Save to backend
      if (onSave) {
        await onSave({ cartAddOns: updatedAddOns });
      } else {
        // For individual updates, we need to save the entire array
        console.log('⚠️ Using fallback delete method');
        // You may need to implement a bulk update method
      }
      
      console.log('✅ Add-on deleted successfully');
      
    } catch (error) {
      console.error('❌ Error deleting add-on:', error);
      // ✅ Revert local state on error
      setCartAddOns(cartAddOns);
      alert('Failed to delete add-on. Please try again.');
    }
  };

  // // ✅ FIXED: Handle updating add-on
  // const handleUpdateAddOn = async (addOnId, updates) => {
  //   try {
  //     console.log('🔄 Updating add-on:', addOnId, updates);
      
  //     // ✅ Update local state immediately
  //     const updatedAddOns = cartAddOns.map(addOn =>
  //       addOn.id === addOnId ? { ...addOn, ...updates } : addOn
  //     );
  //     setCartAddOns(updatedAddOns);
      
  //     // ✅ Save to backend
  //     if (updateCartAddOn) {
  //       await updateCartAddOn(addOnId, updates);
  //     }
      
  //     console.log('✅ Add-on updated successfully');
      
  //   } catch (error) {
  //     console.error('❌ Error updating add-on:', error);
  //     // ✅ Revert local state on error
  //     setCartAddOns(cartAddOns);
  //     alert('Failed to update add-on. Please try again.');
  //   }
  // };


  const handleSaveEditDialog = async () => {
    try {
      const { addOn } = editDialog;
      if (!addOn) return;

      const updatedAddOns = cartAddOns.map(a => 
        a.id === addOn.id ? { ...a, name: addOn.name, icon: addOn.icon } : a
      );
      setCartAddOns(updatedAddOns);

      if (updateCartAddOn) {
        await updateCartAddOn(addOn.id, { name: addOn.name, icon: addOn.icon });
      }

      setEditDialog({ open: false, addOn: null });
      console.log('✅ Add-on metadata updated');
    } catch (error) {
      console.error('Error updating add-on metadata:', error);
    }
  };

  const getIconComponent = (iconValue) => {
    const iconConfig = availableIcons.find(icon => icon.value === iconValue);
    return iconConfig ? iconConfig.icon : <MoneyIcon />;
  };

  const getAddOnIcon = (addOn) => {
    const iconComponent = getIconComponent(addOn.icon || 'MoneyIcon');
    const iconConfig = availableIcons.find(icon => icon.value === (addOn.icon || 'MoneyIcon')) || availableIcons[0];
    const color = iconConfig?.color || posConfigColors.primary;
    
    return React.cloneElement(iconComponent, { sx: { color } });
  };

  // ✅ Get add-on color based on type (matching EnhancedAddAddonDialog)
  const getAddOnColor = (addOn) => {
    // First try to get color from type
    if (addOn.type && addOnTypeColors[addOn.type]) {
      return addOnTypeColors[addOn.type];
    }
    
    // Fallback to category color
    if (addOn.category && addOnCategoryColors[addOn.category]) {
      return addOnCategoryColors[addOn.category];
    }
    
    // Final fallback to icon color or primary
    const iconConfig = availableIcons.find(icon => icon.value === (addOn.icon || 'MoneyIcon'));
    return iconConfig?.color || posConfigColors.primary;
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default' }}>
      {/* ✅ Beautiful Header Section */}
      <Paper 
        elevation={0}
        sx={{ 
          background: `linear-gradient(135deg, ${posConfigColors.primary} 0%, ${posConfigColors.secondary} 100%)`,
          borderRadius: { xs: 2, md: 3 },
          p: { xs: 2.5, sm: 3, md: 4 },
          mb: { xs: 2, md: 3 },
          position: 'relative',
          overflow: 'hidden',
          boxShadow: `0 10px 30px ${posConfigColors.primary}30`,
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="3"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            animation: 'backgroundFloat 20s ease-in-out infinite'
          }
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Grid container spacing={{ xs: 2, md: 3 }} alignItems="center">
            <Grid item xs={12} md={8}>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                gap: { xs: 2, md: 3 }, 
                flexDirection: { xs: 'column', sm: 'row' },
                textAlign: { xs: 'center', sm: 'left' }
              }}>
                <Avatar sx={{ 
                  width: { xs: 56, sm: 64, md: 72 }, 
                  height: { xs: 56, sm: 64, md: 72 },
                  background: 'rgba(255, 255, 255, 0.2)',
                  backdropFilter: 'blur(10px)',
                  border: '2px solid rgba(255, 255, 255, 0.3)'
                }}>
                  <SettingsIcon sx={{ fontSize: { xs: 28, sm: 32, md: 40 }, color: 'white' }} />
                </Avatar>
                <Box>
                  <Typography variant={isMobile ? "h5" : isTablet ? "h4" : "h3"} sx={{ 
                    fontWeight: 800, 
                    color: 'white',
                    mb: 1,
                    textShadow: '0 4px 8px rgba(0,0,0,0.3)',
                    fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem' }
                  }}>
                    {t('posConfig.cartAddOns.headerTitle') || 'Cart Add-ons Management'}
                  </Typography>
                  <Typography variant={isMobile ? "body2" : isTablet ? "h6" : "h6"} sx={{ 
                    color: 'rgba(255, 255, 255, 0.9)',
                    fontWeight: 400,
                    maxWidth: { xs: '100%', md: 600 },
                    lineHeight: 1.6,
                    fontSize: { xs: '0.875rem', md: '1.125rem' }
                  }}>
                    {t('posConfig.cartAddOns.headerDescription') || 'Configure service charges, taxes, discounts, and additional fees that enhance your point-of-sale system'}
                  </Typography>
                </Box>
              </Box>
            </Grid>
            
            <Grid item xs={12} md={4}>
              <Box sx={{ 
                textAlign: { xs: 'center', md: 'right' },
                display: 'flex',
                justifyContent: { xs: 'center', md: 'flex-end' },
                gap: 2
              }}>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={() => setShowEnhancedDialog(true)}
                  size={isMobile ? "medium" : isTablet ? "large" : "large"}
                  sx={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    backdropFilter: 'blur(10px)',
                    border: '2px solid rgba(255, 255, 255, 0.3)',
                    color: 'white',
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: 3,
                    px: { xs: 2, md: 3 },
                    py: { xs: 1, md: 1.5 },
                    fontSize: { xs: '0.875rem', md: '1rem' },
                    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
                    '&:hover': {
                      background: 'rgba(255, 255, 255, 0.3)',
                      transform: 'translateY(-2px)',
                      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)'
                    }
                  }}
                >
                  <AutoAwesomeIcon sx={{ mr: 1, fontSize: { xs: 18, md: 20 } }} />
                  {t('posConfig.cartAddOns.createNew') || 'Create New Add-on'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </Box>
      </Paper>

      {/* ✅ Stats Cards */}
      <Grid container spacing={{ xs: 1.5, md: 2 }} sx={{ mb: { xs: 2, md: 3 } }}>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ 
            p: { xs: 1.5, md: 2 }, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${posConfigColors.success}15 0%, ${posConfigColors.success}05 100%)`,
            border: `1px solid ${posConfigColors.success}20`,
            borderRadius: 2
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: posConfigColors.success, mb: 0.5 }}>
              {cartAddOns.filter(a => a.enabled).length}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {t('posConfig.cartAddOns.stats.active') || 'Active'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ 
            p: { xs: 1.5, md: 2 }, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${posConfigColors.primary}15 0%, ${posConfigColors.primary}05 100%)`,
            border: `1px solid ${posConfigColors.primary}20`,
            borderRadius: 2
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: posConfigColors.primary, mb: 0.5 }}>
              {cartAddOns.length}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {t('posConfig.cartAddOns.stats.total') || 'Total'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ 
            p: { xs: 1.5, md: 2 }, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${posConfigColors.warning}15 0%, ${posConfigColors.warning}05 100%)`,
            border: `1px solid ${posConfigColors.warning}20`,
            borderRadius: 2
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: posConfigColors.warning, mb: 0.5 }}>
              {cartAddOns.filter(a => a.type && a.type.includes('percentage')).length}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {t('posConfig.cartAddOns.stats.percentage') || 'Percentage'}
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6} sm={3}>
          <Paper sx={{ 
            p: { xs: 1.5, md: 2 }, 
            textAlign: 'center',
            background: `linear-gradient(135deg, ${posConfigColors.error}15 0%, ${posConfigColors.error}05 100%)`,
            border: `1px solid ${posConfigColors.error}20`,
            borderRadius: 2
          }}>
            <Typography variant="h4" sx={{ fontWeight: 700, color: posConfigColors.error, mb: 0.5 }}>
              {cartAddOns.filter(a => a.type && (a.type.includes('subtract') || a.type.includes('discount'))).length}
            </Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>
              {t('posConfig.cartAddOns.stats.discounts') || 'Discounts'}
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* ✅ Info Alert */}
      <Alert 
        severity="info" 
        sx={{ 
          mb: isMobile ? 2 : 3,
          borderRadius: 2,
          fontSize: isMobile ? '0.8rem' : '0.875rem',
          border: `1px solid ${posConfigColors.info}30`,
          background: `linear-gradient(135deg, ${posConfigColors.info}08 0%, ${posConfigColors.info}03 100%)`
        }}
        icon={<AutoAwesomeIcon sx={{ color: posConfigColors.info }} />}
      >
        {t('posConfig.cartAddOns.infoAlert') || 'Add-ons will appear in the cart section when enabled. Changes are automatically saved and synced across all devices.'}
      </Alert>

      {/* Add-ons Grid */}
      {cartAddOns.length === 0 ? (
        <Box sx={{ textAlign: 'center', py: 8 }}>
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Avatar sx={{ 
              width: { xs: 80, md: 100 }, 
              height: { xs: 80, md: 100 }, 
              mx: 'auto', 
              mb: 3,
              background: `linear-gradient(135deg, ${posConfigColors.primary}15 0%, ${posConfigColors.secondary}15 100%)`,
              border: `2px solid ${posConfigColors.primary}20`
            }}>
              <SettingsIcon sx={{ fontSize: { xs: 40, md: 50 }, color: posConfigColors.primary }} />
            </Avatar>
            <Typography variant="h5" color="text.secondary" gutterBottom sx={{ fontWeight: 600 }}>
              {t('posConfig.cartAddOns.emptyState.title') || 'No Add-ons Configured'}
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 4, maxWidth: 400, mx: 'auto' }}>
              {t('posConfig.cartAddOns.emptyState.description') || 'Get started by creating your first add-on to enhance your POS system with additional charges, taxes, or discounts.'}
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setShowEnhancedDialog(true)}
              size={isMobile ? "large" : "large"}
              sx={{
                background: `linear-gradient(135deg, ${posConfigColors.primary} 0%, ${posConfigColors.secondary} 100%)`,
                color: 'white',
                fontWeight: 700,
                textTransform: 'none',
                borderRadius: 3,
                px: 4,
                py: 1.5,
                fontSize: '1rem',
                boxShadow: `0 8px 25px ${posConfigColors.primary}30`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${posConfigColors.primary}dd 0%, ${posConfigColors.secondary}dd 100%)`,
                  transform: 'translateY(-2px)',
                  boxShadow: `0 12px 35px ${posConfigColors.primary}40`
                }
              }}
            >
              {t('posConfig.cartAddOns.emptyState.createButton') || 'Create Your First Add-on'}
            </Button>
          </motion.div>
        </Box>
      ) : (
        <>
          {/* ✅ Responsive Grid for Mobile/Tablet/Desktop */}
          <Grid container spacing={isMobile ? 1.5 : isTablet ? 2 : 2.5}>
            <AnimatePresence>
              {cartAddOns.map((addOn, index) => {
                const isEditing = editingAddOns[addOn.id];
                const editData = isEditing || addOn;
                const addOnColor = getAddOnColor(addOn);

                return (
                  <Grid item xs={12} sm={6} md={isTablet ? 6 : 4} lg={4} key={`addon-${addOn.id}-${index}`}>
                    <MotionCard
                      layout
                      initial={{ opacity: 0, y: 20, scale: 0.95 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: -20, scale: 0.95 }}
                      transition={{ duration: 0.4, delay: index * 0.05 }}
                      sx={{
                        height: '100%',
                        background: 'white',
                        borderRadius: isMobile ? 2 : 3,
                        border: `1px solid ${addOnColor}15`,
                        borderLeft: `4px solid ${addOnColor}`,
                        transition: 'all 0.3s ease',
                        position: 'relative',
                        overflow: 'hidden',
                        '&:hover': {
                          boxShadow: `0 8px 32px ${addOnColor}25`,
                          borderColor: `${addOnColor}30`,
                          transform: 'translateY(-4px)'
                        },
                        '&::before': {
                          content: '""',
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          width: '100px',
                          height: '100px',
                          background: `radial-gradient(circle, ${addOnColor}08 0%, transparent 70%)`,
                          borderRadius: '50%',
                          transform: 'translate(30px, -30px)'
                        }
                      }}
                    >
                      <CardContent sx={{ p: isMobile ? 1.5 : isTablet ? 2 : 2.5, position: 'relative', zIndex: 1 }}>
                        {/* Header */}
                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                            <Avatar sx={{ 
                              width: isMobile ? 36 : isTablet ? 40 : 44, 
                              height: isMobile ? 36 : isTablet ? 40 : 44, 
                              background: `linear-gradient(135deg, ${addOnColor}20 0%, ${addOnColor}10 100%)`, 
                              border: `2px solid ${addOnColor}30`,
                              boxShadow: `0 4px 12px ${addOnColor}20`
                            }}>
                              {getAddOnIcon(addOn)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography variant={isMobile ? "subtitle1" : isTablet ? "h6" : "h6"} sx={{ 
                                fontWeight: 700, 
                                color: '#333',
                                mb: 0.5,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: isMobile ? '0.95rem' : isTablet ? '1.05rem' : '1.15rem'
                              }}>
                                {addOn.name}
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center', flexWrap: 'wrap' }}>
                                <Chip
                                  size="small"
                                  label={addOn.type || 'fixed'}
                                  sx={{
                                    background: `${addOnColor}15`,
                                    color: addOnColor,
                                    fontWeight: 600,
                                    fontSize: isMobile ? '0.65rem' : '0.7rem',
                                    height: 18,
                                    textTransform: 'capitalize'
                                  }}
                                />
                                <Typography variant="caption" sx={{ 
                                  color: addOnColor,
                                  fontWeight: 700,
                                  fontSize: isMobile ? '0.7rem' : '0.75rem'
                                }}>
                                  {addOn.type === 'percentage' || addOn.type?.includes('percentage') ? `${addOn.value}%` : `$${addOn.value}`}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                          
                          {/* Action Buttons */}
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            <IconButton 
                              size="small" 
                              onClick={() => handleEditAddOn(addOn)}
                              sx={{ 
                                color: posConfigColors.success,
                                bgcolor: `${posConfigColors.success}10`,
                                width: isMobile ? 28 : isTablet ? 30 : 32,
                                height: isMobile ? 28 : isTablet ? 30 : 32,
                                '&:hover': { 
                                  bgcolor: `${posConfigColors.success}20`,
                                  transform: 'scale(1.1)'
                                }
                              }}
                            >
                              <EditIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                            </IconButton>
                            {addOn.editable && (
                              <IconButton 
                                size="small" 
                                onClick={() => handleDeleteAddOn(addOn.id)}
                                sx={{ 
                                  color: posConfigColors.error,
                                  bgcolor: `${posConfigColors.error}10`,
                                  width: isMobile ? 28 : isTablet ? 30 : 32,
                                  height: isMobile ? 28 : isTablet ? 30 : 32,
                                  '&:hover': { 
                                    bgcolor: `${posConfigColors.error}20`,
                                    transform: 'scale(1.1)'
                                  }
                                }}
                              >
                                <DeleteIcon sx={{ fontSize: isMobile ? 14 : 16 }} />
                              </IconButton>
                            )}
                          </Box>
                        </Box>

                        {/* Status Toggle */}
                        <Box sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          p: isMobile ? 1 : isTablet ? 1.25 : 1.5,
                          bgcolor: `${addOnColor}05`,
                          borderRadius: 2,
                          border: `1px solid ${addOnColor}15`,
                          mb: 2
                        }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                            <Avatar sx={{ 
                              width: isMobile ? 20 : 24, 
                              height: isMobile ? 20 : 24, 
                              bgcolor: addOn.enabled ? `${addOnColor}20` : 'grey.200', 
                              color: addOn.enabled ? addOnColor : 'grey.500' 
                            }}>
                              <StarIcon sx={{ fontSize: isMobile ? 12 : 14 }} />
                            </Avatar>
                            <Box>
                              <Typography variant="caption" sx={{ 
                                fontWeight: 600, 
                                color: addOn.enabled ? '#333' : 'grey.500', 
                                display: 'block',
                                fontSize: isMobile ? '0.7rem' : '0.75rem'
                              }}>
                                {t('posConfig.cartAddOns.status') || 'Status'}
                              </Typography>
                              <Typography variant="caption" sx={{ 
                                color: addOn.enabled ? addOnColor : 'grey.400', 
                                fontWeight: 600, 
                                fontSize: isMobile ? '0.65rem' : '0.7rem'
                              }}>
                                {addOn.enabled ? (t('common.enabled') || 'Enabled') : (t('common.disabled') || 'Disabled')}
                              </Typography>
                            </Box>
                          </Box>
                          <Switch
                            checked={addOn.enabled}
                            onChange={(e) => handleToggleEnabled(addOn.id, e.target.checked)}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{
                              '& .MuiSwitch-switchBase.Mui-checked': {
                                color: addOnColor,
                                '& + .MuiSwitch-track': { backgroundColor: addOnColor }
                              }
                            }}
                          />
                        </Box>

                        {/* Editable Fields */}
                        {addOn.editable && (
                          <Box sx={{ pt: 1, borderTop: `1px solid ${addOnColor}20` }}>
                            <TextField
                              fullWidth
                              label={addOn.type === 'percentage' || addOn.type?.includes('percentage') ? 
                                (t('posConfig.cartAddOns.fields.percentageLabel') || 'Percentage (%)') : 
                                (t('posConfig.cartAddOns.fields.fixedLabel') || 'Fixed Amount ($)')
                              }
                              type="number"
                              size={isMobile ? "small" : "medium"}
                              value={isEditing ? editData.value : addOn.value}
                              onChange={(e) => {
                                if (isEditing) {
                                  handleEditChange(addOn.id, 'value', e.target.value);
                                } else {
                                  handleStartEdit(addOn.id);
                                  handleEditChange(addOn.id, 'value', e.target.value);
                                }
                              }}
                              InputProps={{
                                startAdornment: (
                                  <InputAdornment position="start">
                                    {addOn.type === 'percentage' || addOn.type?.includes('percentage') ? 
                                      <PercentIcon sx={{ color: addOnColor, fontSize: 16 }} /> : 
                                      <MoneyIcon sx={{ color: addOnColor, fontSize: 16 }} />
                                    }
                                  </InputAdornment>
                                ),
                              }}
                              sx={{ 
                                mb: 1.5,
                                '& .MuiOutlinedInput-root': {
                                  borderRadius: 2,
                                  fontSize: isMobile ? '0.875rem' : '1rem',
                                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                                    borderColor: addOnColor,
                                    borderWidth: 2
                                  }
                                },
                                '& .MuiInputLabel-root.Mui-focused': { color: addOnColor }
                              }}
                              inputProps={{
                                min: 0,
                                max: addOn.type === 'percentage' || addOn.type?.includes('percentage') ? 100 : 9999,
                                step: addOn.type === 'percentage' || addOn.type?.includes('percentage') ? 0.1 : 0.01
                              }}
                            />

                            {/* Action Buttons */}
                            <AnimatePresence>
                              {isEditing && (
                                <MotionBox
                                  initial={{ opacity: 0, y: 10 }}
                                  animate={{ opacity: 1, y: 0 }}
                                  exit={{ opacity: 0, y: -10 }}
                                  sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}
                                >
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    onClick={() => handleCancelEdit(addOn.id)}
                                    sx={{
                                      borderColor: 'grey.300',
                                      color: 'grey.600',
                                      fontSize: isMobile ? '0.65rem' : '0.7rem',
                                      minWidth: 'auto',
                                      px: 1.5,
                                      height: isMobile ? 28 : 32
                                    }}
                                  >
                                    {t('common.cancel') || 'Cancel'}
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="contained"
                                    onClick={() => handleSaveAddOn(addOn.id)}
                                    sx={{
                                      background: addOnColor,
                                      fontSize: isMobile ? '0.65rem' : '0.7rem',
                                      minWidth: 'auto',
                                      px: 1.5,
                                      height: isMobile ? 28 : 32,
                                      '&:hover': { background: `${addOnColor}dd` }
                                    }}
                                  >
                                    {t('common.save') || 'Save'}
                                  </Button>
                                </MotionBox>
                              )}
                            </AnimatePresence>
                          </Box>
                        )}

                        {/* Status Chips */}
                        <Box sx={{ display: 'flex', gap: 0.5, mt: 1.5, flexWrap: 'wrap' }}>
                          <Chip
                            label={addOn.enabled ? (t('common.active') || 'Active') : (t('common.inactive') || 'Inactive')}
                            size="small"
                            sx={{
                              background: addOn.enabled ? `${addOnColor}15` : 'grey.100',
                              color: addOn.enabled ? addOnColor : 'grey.500',
                              fontWeight: 600,
                              fontSize: isMobile ? '0.6rem' : '0.65rem',
                              height: isMobile ? 18 : 20,
                              border: `1px solid ${addOn.enabled ? addOnColor + '30' : 'grey.300'}`
                            }}
                          />
                          <Chip
                            label={addOn.type === 'percentage' || addOn.type?.includes('percentage') ? `${addOn.value}%` : `$${addOn.value}`}
                            size="small"
                            sx={{
                              background: `${addOnColor}20`,
                              color: addOnColor,
                              fontWeight: 700,
                              fontSize: isMobile ? '0.6rem' : '0.65rem',
                              height: isMobile ? 18 : 20,
                              border: `1px solid ${addOnColor}40`
                            }}
                          />
                        </Box>
                      </CardContent>
                    </MotionCard>
                  </Grid>
                );
              })}
            </AnimatePresence>
          </Grid>
        </>
      )}

      {/* ✅ Enhanced Add-on Dialog (Now with isMobile AND isTablet support) */}
      <EnhancedAddAddonDialog
        open={showEnhancedDialog}
        onClose={() => setShowEnhancedDialog(false)}
        onSave={handleCreateAddOn}
        isMobile={isMobile}
        isTablet={isTablet}
      />

      {/* Edit Add-on Dialog */}
      <Dialog 
        open={editDialog.open} 
        onClose={() => setEditDialog({ open: false, addOn: null })} 
        maxWidth="sm" 
        fullWidth={!isMobile}
        fullScreen={isMobile}
        PaperProps={{ sx: { borderRadius: isMobile ? 0 : 3 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ 
              bgcolor: posConfigColors.success, 
              width: isMobile ? 32 : isTablet ? 36 : 40, 
              height: isMobile ? 32 : isTablet ? 36 : 40 
            }}>
              <EditIcon sx={{ fontSize: isMobile ? 16 : 20 }} />
            </Avatar>
            <Typography variant={isMobile ? "h6" : "h5"} sx={{ fontWeight: 700, color: '#333' }}>
              {t('posConfig.cartAddOns.editTitle') || 'Edit Add-on'}
            </Typography>
          </Box>
        </DialogTitle>
        
        <DialogContent>
          <TextField
            fullWidth
            label={t('posConfig.cartAddOns.fields.nameLabel') || 'Add-on Name'}
            value={editDialog.addOn?.name || ''}
            onChange={(e) => setEditDialog(prev => ({ ...prev, addOn: { ...prev.addOn, name: e.target.value } }))}
            size={isMobile ? "small" : "medium"}
            sx={{ mb: 2, mt: 1 }}
          />

          <FormControl fullWidth size={isMobile ? "small" : "medium"}>
            <InputLabel>{t('posConfig.cartAddOns.fields.iconLabel') || 'Icon'}</InputLabel>
            <Select
              value={editDialog.addOn?.icon || 'MoneyIcon'}
              label={t('posConfig.cartAddOns.fields.iconLabel') || 'Icon'}
              onChange={(e) => setEditDialog(prev => ({ ...prev, addOn: { ...prev.addOn, icon: e.target.value } }))}
              renderValue={(value) => {
                const selectedIcon = availableIcons.find(icon => icon.value === value);
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Avatar sx={{ width: 20, height: 20, bgcolor: `${selectedIcon?.color}15` }}>
                      {selectedIcon ? React.cloneElement(selectedIcon.icon, { sx: { fontSize: 12, color: selectedIcon.color } }) : <MoneyIcon />}
                    </Avatar>
                    {selectedIcon?.name || (t('posConfig.cartAddOns.fields.selectIcon') || 'Select Icon')}
                  </Box>
                );
              }}
            >
              {availableIcons.map((iconOption) => (
                <MenuItem key={iconOption.value} value={iconOption.value}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                    <Avatar sx={{ width: 24, height: 24, bgcolor: `${iconOption.color}15` }}>
                      {React.cloneElement(iconOption.icon, { sx: { fontSize: 14, color: iconOption.color } })}
                    </Avatar>
                    <Typography variant="body2">{iconOption.name}</Typography>
                  </Box>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        
        <DialogActions sx={{ p: isMobile ? 2 : 3, gap: 1 }}>
          <Button 
            onClick={() => setEditDialog({ open: false, addOn: null })}
            variant="outlined"
            size={isMobile ? "small" : "medium"}
          >
            {t('common.cancel') || 'Cancel'}
          </Button>
          <Button 
            onClick={handleSaveEditDialog}
            variant="contained"
            size={isMobile ? "small" : "medium"}
            sx={{ background: `linear-gradient(135deg, ${posConfigColors.success} 0%, ${posConfigColors.success}dd 100%)` }}
          >
            {t('common.save') || 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ✅ Add keyframe animation for background float */}
      <style>{`
        @keyframes backgroundFloat {
          0%, 100% { transform: translateY(0px) rotate(0deg); }
          50% { transform: translateY(-10px) rotate(5deg); }
        }
      `}</style>
    </Box>
  );
};

export default CartAddOnsManager;